import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Search,
  MapPin,
  Clock,
  Route as RouteIcon,
  Star,
  Mountain,
  ChevronRight,
  Loader2,
  Compass,
  SlidersHorizontal,
  Database,
  ArrowLeft,
  Navigation,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTY_CONFIG = {
  easy: { label: 'Leicht', color: 'bg-green-500/10 text-green-500 border-green-500/30' },
  medium: { label: 'Mittel', color: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  hard: { label: 'Anspruchsvoll', color: 'bg-red-500/10 text-red-500 border-red-500/30' },
};

const VEHICLE_TYPES = [
  { value: 'oldtimer', label: 'Oldtimer' },
  { value: 'caravan', label: 'Caravan' },
  { value: 'all', label: 'Alle Fahrzeuge' },
];

const REGIONS = [
  'Alle Regionen',
  'Alpen',
  'Schwarzwald',
  'Mosel',
  'Eifel',
  'Harz',
  'Bayerischer Wald',
  'Nordsee',
  'Ostsee',
  'S\u00E4chsische Schweiz',
  'Allg\u00E4u',
];

const StarDisplay = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-3.5 h-3.5 ${
          star <= (rating || 0) ? 'text-primary fill-primary' : 'text-muted-foreground/30'
        }`}
      />
    ))}
  </div>
);

const ScenicRoutes = ({ onSelectRoute }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('Alle Regionen');
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailRoute, setDetailRoute] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (selectedRegion !== 'Alle Regionen') params.region = selectedRegion;
      if (selectedDifficulty) params.difficulty = selectedDifficulty;
      if (selectedVehicle) params.vehicle_type = selectedVehicle;

      const response = await axios.get(`${API}/scenic-routes`, { params });
      setRoutes(Array.isArray(response.data) ? response.data : response.data.routes || []);
    } catch (error) {
      console.error('Failed to fetch scenic routes:', error);
      toast.error('Routen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedRegion, selectedDifficulty, selectedVehicle]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const fetchRouteDetail = async (id) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`${API}/scenic-routes/${id}`);
      setDetailRoute(response.data);
    } catch (error) {
      console.error('Failed to fetch route detail:', error);
      toast.error('Routendetails konnten nicht geladen werden');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectRoute = () => {
    if (!detailRoute) return;
    onSelectRoute({
      start: detailRoute.start,
      end: detailRoute.end,
      waypoints: detailRoute.waypoints || [],
    });
    setDetailRoute(null);
    toast.success('Route \u00FCbernommen');
  };

  const handleSeed = async () => {
    try {
      setSeeding(true);
      await axios.post(`${API}/scenic-routes/seed`);
      toast.success('Beispielrouten wurden hinzugef\u00FCgt');
      fetchRoutes();
    } catch (error) {
      console.error('Failed to seed routes:', error);
      toast.error('Seed fehlgeschlagen');
    } finally {
      setSeeding(false);
    }
  };

  const toggleDifficulty = (diff) => {
    setSelectedDifficulty((prev) => (prev === diff ? null : diff));
  };

  const toggleVehicle = (type) => {
    setSelectedVehicle((prev) => (prev === type ? null : type));
  };

  return (
    <div className="h-full bg-background">
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
              <Compass className="w-8 h-8 text-primary" />
              Panoramarouten
            </h1>
            <p className="text-muted-foreground mt-2">
              Entdecke kuratierte Strecken f\u00FCr Oldtimer und Caravans
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-border text-muted-foreground hover:text-foreground"
            onClick={handleSeed}
            disabled={seeding}
          >
            {seeding ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Database className="w-4 h-4 mr-2" />
            )}
            Seed Routes
          </Button>
        </div>

        {/* Search & Filters */}
        <div className="space-y-4 mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Routen suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-muted/50 border-border h-12"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-muted-foreground">
              <SlidersHorizontal className="w-4 h-4" />
              <span className="text-sm">Filter:</span>
            </div>

            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-[180px] bg-muted/50 border-border h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {REGIONS.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Separator orientation="vertical" className="h-6 bg-border" />

            {Object.entries(DIFFICULTY_CONFIG).map(([key, config]) => (
              <Badge
                key={key}
                variant="outline"
                className={`cursor-pointer transition-all active:scale-95 ${
                  selectedDifficulty === key
                    ? config.color
                    : 'border-border text-muted-foreground hover:border-foreground/30'
                }`}
                onClick={() => toggleDifficulty(key)}
              >
                {config.label}
              </Badge>
            ))}

            <Separator orientation="vertical" className="h-6 bg-border" />

            {VEHICLE_TYPES.map((type) => (
              <Badge
                key={type.value}
                variant="outline"
                className={`cursor-pointer transition-all active:scale-95 ${
                  selectedVehicle === type.value
                    ? 'bg-primary/10 text-primary border-primary/30'
                    : 'border-border text-muted-foreground hover:border-foreground/30'
                }`}
                onClick={() => toggleVehicle(type.value)}
              >
                {type.label}
              </Badge>
            ))}
          </div>
        </div>

        {/* Routes Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : routes.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Compass className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                Keine Routen gefunden
              </h3>
              <p className="text-muted-foreground">
                Versuche andere Filtereinstellungen oder f\u00FCge Beispielrouten hinzu.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-[calc(100vh-320px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pr-4">
              {routes.map((route) => (
                <Card
                  key={route.id}
                  className="bg-card border-border hover:border-primary/30 hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                  onClick={() => fetchRouteDetail(route.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-display text-lg text-foreground leading-tight">
                        {route.name}
                      </CardTitle>
                      {route.difficulty && DIFFICULTY_CONFIG[route.difficulty] && (
                        <Badge
                          variant="outline"
                          className={DIFFICULTY_CONFIG[route.difficulty].color}
                        >
                          {DIFFICULTY_CONFIG[route.difficulty].label}
                        </Badge>
                      )}
                    </div>
                    {route.region && (
                      <CardDescription className="flex items-center gap-1.5 mt-1">
                        <MapPin className="w-3 h-3" />
                        {route.region}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-4 mb-3">
                      {route.distance && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <RouteIcon className="w-3.5 h-3.5" />
                          <span>{route.distance} km</span>
                        </div>
                      )}
                      {route.duration && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{route.duration}</span>
                        </div>
                      )}
                    </div>

                    {route.rating != null && (
                      <div className="mb-3">
                        <StarDisplay rating={route.rating} />
                      </div>
                    )}

                    {route.highlights?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {route.highlights.slice(0, 3).map((hl, i) => (
                          <Badge
                            key={i}
                            variant="secondary"
                            className="text-xs bg-muted text-muted-foreground"
                          >
                            {hl}
                          </Badge>
                        ))}
                        {route.highlights.length > 3 && (
                          <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                            +{route.highlights.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    {route.season && (
                      <div className="text-xs text-muted-foreground">
                        Beste Zeit: {route.season}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detailRoute} onOpenChange={() => setDetailRoute(null)}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : detailRoute ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setDetailRoute(null)}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <DialogTitle className="font-display text-xl text-foreground">
                    {detailRoute.name}
                  </DialogTitle>
                </div>
                <DialogDescription className="flex items-center gap-2">
                  {detailRoute.region && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {detailRoute.region}
                    </span>
                  )}
                  {detailRoute.difficulty && DIFFICULTY_CONFIG[detailRoute.difficulty] && (
                    <Badge
                      variant="outline"
                      className={DIFFICULTY_CONFIG[detailRoute.difficulty].color}
                    >
                      {DIFFICULTY_CONFIG[detailRoute.difficulty].label}
                    </Badge>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* Stats */}
                <div className="flex flex-wrap gap-6">
                  {detailRoute.distance && (
                    <div className="flex items-center gap-2 text-sm">
                      <RouteIcon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-foreground font-medium">{detailRoute.distance} km</span>
                    </div>
                  )}
                  {detailRoute.duration && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-foreground font-medium">{detailRoute.duration}</span>
                    </div>
                  )}
                  {detailRoute.elevation_gain && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mountain className="w-4 h-4 text-muted-foreground" />
                      <span className="text-foreground font-medium">{detailRoute.elevation_gain} m</span>
                    </div>
                  )}
                </div>

                {detailRoute.rating != null && <StarDisplay rating={detailRoute.rating} />}

                {/* Description */}
                {detailRoute.description && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Beschreibung</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {detailRoute.description}
                    </p>
                  </div>
                )}

                {/* Highlights */}
                {detailRoute.highlights?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Highlights</h4>
                    <div className="flex flex-wrap gap-2">
                      {detailRoute.highlights.map((hl, i) => (
                        <Badge key={i} variant="outline" className="bg-primary/10 text-primary border-primary/30">
                          {hl}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Waypoints */}
                {detailRoute.waypoints?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Wegpunkte</h4>
                    <div className="space-y-2">
                      {detailRoute.waypoints.map((wp, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium flex-shrink-0">
                            {i + 1}
                          </div>
                          <span className="text-foreground">{wp.name || `${wp.lat}, ${wp.lng}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailRoute.season && (
                  <div className="text-sm text-muted-foreground">
                    Empfohlene Reisezeit: <span className="text-foreground">{detailRoute.season}</span>
                  </div>
                )}

                {/* Action */}
                <Button
                  onClick={handleSelectRoute}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg active:scale-95 transition-all"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Route \u00FCbernehmen
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScenicRoutes;
